import React, { useEffect, useState } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import { useTranslation } from 'react-i18next';
import DeckDetail from '../models/DeckDetail.js';

const socket = io('https://amora-backend.onrender.com');

const MyDecks = () => {
  const { t } = useTranslation();
  const [myDecks, setMyDecks] = useState([]);
  const [sharedDecks, setSharedDecks] = useState([]);
  const [newDeckTitle, setNewDeckTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedDeckId, setSelectedDeckId] = useState(null);

  const fetchDecks = async () => {
    try {
      const res = await axios.get('https://amora-backend.onrender.com/api/student/decks');
      const allDecks = res.data;
      const mine = allDecks.filter((d) => !d.shared && !d.teacherName);
      const shared = allDecks.filter((d) => d.shared || d.teacherName);
      setMyDecks(mine);
      setSharedDecks(shared);
    } catch (err) {
      console.error('Failed to fetch decks:', err);
    }
  };

  useEffect(() => {
    fetchDecks();
    socket.on('deck-updated', fetchDecks);
    socket.on('teacher-deck-updated', ({ deckTitle }) => {
      fetchDecks();
    });

    const handleEsc = (e) => {
      if (e.key === 'Escape') setSelectedDeckId(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      socket.off('deck-updated');
      socket.off('teacher-deck-updated');
      window.removeEventListener('keydown', handleEsc);
    };
  }, []);

  const handleCreateDeck = async (e) => {
    e.preventDefault();
    const title = newDeckTitle.trim();
    if (!title) return;
  
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('❌ No token found in localStorage');
      return;
    }
  
    try {
      setLoading(true);
      console.log('Sending title:', title);
  
      await axios.post(
        'http://localhost:3001/api/student/decks',
        { title },
        {
          headers: {
            Authorization: `Bearer ${token}`, // ✅ IMPORTANT!
          },
        }
      );

      setNewDeckTitle('');
      await fetchDecks();
    } catch (err) {
      console.error('Error creating deck:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDeck = async (deckId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found');
      return;
    }
  
    try {
      await axios.delete(`https://amora-backend.onrender.com/api/student/decks/${deckId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      await fetchDecks(); // Refresh decks after deletion
    } catch (err) {
      console.error('Error deleting deck:', err.response?.data || err.message);
    }
  };
  

  return (
    <div className="p-6 text-black">
      <h2 className="text-2xl font-extrabold uppercase border-b border-black mb-6">
         {t('dashboard.decks')}
      </h2>

      <form onSubmit={handleCreateDeck} className="mb-10 flex gap-3 max-w-xl">
        <input
          type="text"
          placeholder={t('aiDeck.newDeck')}
          value={newDeckTitle}
          onChange={(e) => setNewDeckTitle(e.target.value)}
          className="flex-1 border border-black px-4 py-2 uppercase font-bold tracking-wide bg-white text-black"
        />
        <button
          type="submit"
          disabled={loading}
          className="border border-black bg-white text-black px-4 py-2 font-bold uppercase tracking-wide hover:bg-black hover:text-white transition"
        >
          ➕ {t('dashboard.createDeck')}
        </button>
      </form>

      <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
  {myDecks.map((deck) => (
    <div
      key={deck._id}
      onClick={() => setSelectedDeckId(deck._id)}
      className="cursor-pointer border border-black bg-white p-4 font-bold uppercase tracking-wide hover:bg-black hover:text-white transition"
    >
      <h4 className="text-lg mb-2">{deck.title}</h4>
      <p className="text-sm mb-4">{deck.events.length} {t('cards.category')}</p>

      <button
      onClick={(e) => {
        e.stopPropagation();
        const confirmed = window.confirm('Are you sure you want to delete this deck?');
        if (confirmed) {
          handleDeleteDeck(deck._id);
        }
      }}      
        className="w-full border border-black bg-white text-black px-3 py-2 text-xs font-bold uppercase hover:bg-red-600 hover:text-white transition"
      >
       {t('cards.delete')}
      </button>
    </div>
  ))}
</div>

      {/* Modal View */}
      {selectedDeckId && (
  <div className="fixed inset-0 bg-white bg-opacity-95 z-50 flex items-center justify-center p-4">
    <div className="relative w-full max-w-6xl max-h-[90vh] overflow-y-auto border border-black bg-white text-black p-6 shadow-none rounded-none">
      <button
        onClick={() => setSelectedDeckId(null)}
        className="absolute top-3 right-4 text-black text-xl font-bold"
        aria-label="Close deck"
      >
        ✖
      </button>

      {/* 🔁 Make sure DeckDetail doesn't include its own close button */}
      <DeckDetail deckId={selectedDeckId} onBack={() => setSelectedDeckId(null)} />
    </div>
  </div>
)}



      {/* 💡 Optional UI Alternatives: */}
      {/*
        1. Slide-In Panel:
          Replace modal with:
          <div className="fixed right-0 top-0 w-full sm:w-[400px] h-full bg-white border-l border-black p-6 overflow-y-auto z-50">

        2. Flashcard Mode:
          Create full-screen cards:
            - Left/Right arrow navigation
            - Show one event at a time
            - Add 'Back' or 'Exit' button on top
      */}
    </div>
  );
};

export default MyDecks;
